import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Icon from "../../../components/AppIcon";
import {Button} from "../../../components/ui/Button";
import {Input} from "../../../components/ui/Input";
import apiService from "../../../services/api";

const AddParticipantModal = ({
  isOpen,
  onClose,
  onAddParticipant,
  existingParticipants = [],
  eventId,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm();

  const searchEmail = watch("email");

  // Search function - API implementation
  const handleEmailSearch = async (email) => {
    if (!email || email?.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    try {
      const response = await apiService.searchParticipants(eventId, email);
      if (response.success) {
        // Filter out existing participants
        const filtered = response.data.users?.filter(user => 
          !existingParticipants?.some(p => p?.email === user?.email)
        );

        setSearchResults(filtered);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching participants:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchEmail) {
        handleEmailSearch(searchEmail);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchEmail, eventId, existingParticipants]);

  const onSubmit = async (data) => {
    try {
      // Add participant via API
      const response = await apiService.addParticipant(eventId, data.manualEmail);
      if (response.success) {
        // If the API returns the participant data, use that; otherwise, create a mock object
        const newParticipant = response.data?.participant || {
          id: Date.now()?.toString(),
          name: data?.name,
          email: data?.manualEmail,
          avatar: `https://randomuser.me/api/portraits/${
            Math.random() > 0.5 ? "men" : "women"
          }/${Math.floor(Math.random() * 50) + 1}.jpg`,
          joinedAt: new Date()?.toISOString(),
        };
        await onAddParticipant(newParticipant);
        handleClose();
      } else {
        alert(response.message || 'Failed to add participant');
      }
    } catch (error) {
      console.error("Error adding participant:", error);
      alert(error.message || 'Failed to add participant');
    }
  };

  const handleSelectUser = (user) => {
    onAddParticipant(user);
    handleClose();
  };

  const handleClose = () => {
    reset();
    setSearchResults([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg border border-border w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            Add Participant
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Search by Email */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">
              Search by Email
            </h3>
            <Input
              label="Email Address"
              type="email"
              placeholder="Enter email to search..."
              {...register("email", {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              error={errors?.email?.message}
            />

            {/* Search Results */}
            {isSearching && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">
                  Searching...
                </span>
              </div>
            )}

            {searchResults?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Found {searchResults?.length} user
                  {searchResults?.length !== 1 ? "s" : ""}
                </p>
                {searchResults?.map((user) => (
                  <div
                    key={user?.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-micro cursor-pointer"
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-foreground">
                          {user?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {user?.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Plus"
                        className="text-primary hover:text-primary"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchEmail && searchEmail?.length >= 3 && !isSearching && searchResults?.length === 0 && (
              <div className="text-center py-4">
                <Icon name="UserX" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No users found with that email</p>
                <p className="text-xs text-muted-foreground mt-1">You can add them manually below</p>
              </div>
            )}
          </div>

          {/* Manual Add Form */}
          <div className="pt-6 border-t border-border">
            <h3 className="text-sm font-medium text-foreground mb-4">
              Add Manually
            </h3>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                label="Full Name"
                placeholder="Enter participant's name"
                error={errors?.name?.message}
                required
                {...register("name", {
                  required: "Name is required",
                  minLength: {
                    value: 2,
                    message: "Name must be at least 2 characters",
                  },
                })}
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="Enter participant's email"
                error={errors?.manualEmail?.message}
                required
                {...register("manualEmail", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                  validate: (value) => {
                    const exists = existingParticipants?.some(
                      (p) => p?.email?.toLowerCase() === value?.toLowerCase()
                    );
                    return !exists || "This participant is already added";
                  },
                })}
              />

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" loading={isSubmitting} className="flex-1">
                  Add Participant
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddParticipantModal;
